import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import ChannelMessagesService from '@/services/channelMessages.service';
import ChannelsService from '@/services/channels.service';

import { resolveChannelOwnerProfileType, resolveImageId } from './utils/channelMessageHelpers';
import ChannelMenu from './ChannelMenu';

import './styles/ChannelLayout.css';

function getMessageId(message) {
  if (!message) return null;
  return (
    message._id ||
    message.id ||
    message.message_id ||
    (typeof message === 'string' ? message : null)
  );
}

function getTimeValue(value) {
  if (!value) return 0;
  const date = new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? 0 : time;
}

function resolveChannelId(raw) {
  if (!raw) return null;
  if (typeof raw === 'string' || typeof raw === 'number') {
    return String(raw);
  }
  if (typeof raw === 'object') {
    if (raw.$oid) {
      return String(raw.$oid);
    }
    if (raw._id) {
      const nested = resolveChannelId(raw._id);
      if (nested) return nested;
    }
    if (raw.id) {
      const nested = resolveChannelId(raw.id);
      if (nested) return nested;
    }
    if (raw.channelId) {
      const nested = resolveChannelId(raw.channelId);
      if (nested) return nested;
    }
    if (raw.channel_id) {
      const nested = resolveChannelId(raw.channel_id);
      if (nested) return nested;
    }
  }
  return null;
}

function parseUnreadCount(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.max(0, value) : 0;
  }
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed)) {
      return Math.max(0, parsed);
    }
  }
  return 0;
}

function resolveSummaryOwnerImage(entry, { currentUserId, currentUserImage } = {}) {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const directOwnerImage =
    resolveImageId(entry?.channel?.owner?.user_image)
    || resolveImageId(entry?.owner?.user_image)
    || resolveImageId(entry?.ownerImage)
    || resolveImageId(entry?.owner_image)
    || null;

  if (directOwnerImage) {
    return directOwnerImage;
  }

  const avatarCandidate = resolveImageId(entry?.avatar);
  if (avatarCandidate) {
    const coverCandidate =
      resolveImageId(entry?.channel?.image)
      || resolveImageId(entry?.image)
      || resolveImageId(entry?.channelImage)
      || null;
    if (!coverCandidate || avatarCandidate !== coverCandidate) {
      return avatarCandidate;
    }
  }

  const ownerId = entry?.ownerId || entry?.channel?.owner?._id || entry?.channel?.owner || null;
  if (ownerId && currentUserId && String(ownerId) === String(currentUserId)) {
    return resolveImageId(currentUserImage);
  }

  return null;
}

function buildMessagePreviewText(message) {
  if (!message || typeof message !== 'object') return '';
  if (message.message_sticker) return 'Sticker';
  const text = message.message_text ?? message.text ?? '';
  if (typeof text === 'string') {
    const trimmed = text.trim();
    if (trimmed) return trimmed;
  }
  if (Array.isArray(message.attachments) && message.attachments.length > 0) {
    return 'Attachment';
  }
  return '';
}

function areSummaryEntriesEqual(prev, next) {
  if (!prev && !next) return true;
  if (!prev || !next) return false;
  const prevMessageId = getMessageId(prev.lastMessage);
  const nextMessageId = getMessageId(next.lastMessage);
  if (prevMessageId !== nextMessageId) return false;
  const prevUnread = prev?.unreadCount ?? 0;
  const nextUnread = next?.unreadCount ?? 0;
  if (prevUnread !== nextUnread) return false;
  const prevActivity = getTimeValue(prev?.lastActivity);
  const nextActivity = getTimeValue(next?.lastActivity);
  if (prevActivity !== nextActivity) return false;
  const prevReadAt = getTimeValue(prev?.readState?.lastReadAt);
  const nextReadAt = getTimeValue(next?.readState?.lastReadAt);
  if (prevReadAt !== nextReadAt) return false;
  const prevOwnerImage = resolveImageId(prev?.ownerImage) || null;
  const nextOwnerImage = resolveImageId(next?.ownerImage) || null;
  if (prevOwnerImage !== nextOwnerImage) return false;
  const prevAvatar = resolveImageId(prev?.avatar) || null;
  const nextAvatar = resolveImageId(next?.avatar) || null;
  if (prevAvatar !== nextAvatar) return false;
  const prevOwnerType = prev?.ownerType || null;
  const nextOwnerType = next?.ownerType || null;
  if (prevOwnerType !== nextOwnerType) return false;
  const prevOwnerUsername = prev?.ownerUsername || null;
  const nextOwnerUsername = next?.ownerUsername || null;
  if (prevOwnerUsername !== nextOwnerUsername) return false;
  const prevTitle = prev?.title || null;
  const nextTitle = next?.title || null;
  if (prevTitle !== nextTitle) return false;
  return true;
}

function areSummaryMapsEqual(prevMap, nextMap) {
  const prevKeys = Object.keys(prevMap || {});
  const nextKeys = Object.keys(nextMap || {});
  if (prevKeys.length !== nextKeys.length) return false;
  for (const key of prevKeys) {
    if (!Object.prototype.hasOwnProperty.call(nextMap, key)) return false;
    if (!areSummaryEntriesEqual(prevMap[key], nextMap[key])) return false;
  }
  return true;
}

const MEMBERSHIP_SUMMARY_POLL_MS = 10000;
const MEMBERSHIP_SUMMARY_CACHE_MS = 10000;
const OWNER_SUMMARY_CACHE_MS = 10000;

export default function ChannelLayout() {
  const t = useTranslations('common.profile.channels');
  const { user_username, creator_username, ownerUsername } = useParams();

  const [messagesType, setMessagesType] = useState('channels');
  const [memberChannels, setMemberChannels] = useState([]);
  const [loading, setLoading] = useState(true);

  const { state, stateHandler } = useStateHandler();
  const stateHandlerRef = useRef(stateHandler);
  useEffect(() => {
    stateHandlerRef.current = stateHandler;
  }, [stateHandler]);

  const ownerSummaryCacheRef = useRef({ timestamp: 0, data: [] });
  const ownerFetchPromiseRef = useRef(null);
  const membershipSummaryCacheRef = useRef({ timestamp: 0, data: [], totalDocs: 0 });
  const membershipFetchPromiseRef = useRef(null);

  useEffect(() => {
    document.body.classList.add('channels-layout-page');
    return () => {
      document.body.classList.remove('channels-layout-page');
    };
  }, []);

  useEffect(() => {
    ownerSummaryCacheRef.current = { timestamp: 0, data: [] };
    ownerFetchPromiseRef.current = null;
    membershipSummaryCacheRef.current = { timestamp: 0, data: [], totalDocs: 0 };
    membershipFetchPromiseRef.current = null;
  }, [state?.user_session?._id]);

  const setGlobalSummaries = useCallback((summariesArray) => {
    const handler = stateHandlerRef.current;
    if (!handler) return;
    const currentUserId = state?.user_session?._id || null;
    const currentUserImage = state?.user_session?.user_image || null;
    const normalized = {};
    (Array.isArray(summariesArray) ? summariesArray : []).forEach((entry) => {
      if (!entry) return;
      const key =
        resolveChannelId(entry.channelId)
        || resolveChannelId(entry?.channel?._id)
        || null;
      if (!key) return;
      const lastActivity = entry.lastActivity
        || entry?.lastMessage?.createdAt
        || entry?.lastMessage?.created_at
        || entry?.channel?.updatedAt
        || entry?.channel?.createdAt
        || null;
      const lastMessage = entry?.lastMessage && typeof entry.lastMessage === 'object'
        ? { ...entry.lastMessage }
        : entry?.lastMessage || null;
      const previewText = typeof entry.previewText === 'string'
        ? entry.previewText
        : buildMessagePreviewText(lastMessage);
      const resolvedOwnerType = resolveChannelOwnerProfileType(
        entry.channelKind
        || entry.channel_kind
        || entry?.channelKind
        || entry?.channel_kind
        || entry?.channel?.channel_kind
        || entry?.channel?.channelKind
        || entry.ownerType
        || entry?.channel?.owner?.user_type
        || entry?.channel?.owner?.__t
        || (
          (entry.ownerId || entry?.channel?.owner?._id || entry?.channel?.owner)
          && String(entry.ownerId || entry?.channel?.owner?._id || entry?.channel?.owner) === String(state?.user_session?._id)
            ? (state?.user_session?.user_type || state?.user_session?.__t)
            : ''
        ),
        null
      );

      normalized[key] = {
        ...entry,
        channelId: key,
        lastActivity,
        lastMessage,
        ownerId: entry.ownerId || entry?.channel?.owner?._id || entry?.channel?.owner || null,
        ownerUsername:
          entry.ownerUsername
          || entry?.channel?.owner_username
          || entry?.channel?.owner?.user_username
          || (
            (entry.ownerId || entry?.channel?.owner?._id || entry?.channel?.owner)
            && String(entry.ownerId || entry?.channel?.owner?._id || entry?.channel?.owner) === String(state?.user_session?._id)
              ? state?.user_session?.user_username
              : null
          ),
        ownerType: resolvedOwnerType || null,
        ownerImage: resolveSummaryOwnerImage(entry, { currentUserId, currentUserImage }),
        title:
          entry.title
          || entry?.channel?.title
          || entry?.channel?.channel_name
          || (
            (entry.ownerId || entry?.channel?.owner?._id || entry?.channel?.owner)
            && String(entry.ownerId || entry?.channel?.owner?._id || entry?.channel?.owner) === String(state?.user_session?._id)
              ? state?.user_session?.channel_name
              : null
          )
          || entry.ownerUsername
          || null,
        unreadCount: parseUnreadCount(entry.unreadCount),
        previewText,
        readState: entry.readState || null,
      };
    });

    const previous = handler.getValue('channelSummaries') || {};
    if (!areSummaryMapsEqual(previous, normalized)) {
      handler.set('channelSummaries', normalized);
    }
  }, [
    state?.user_session?._id,
    state?.user_session?.channel_name,
    state?.user_session?.user_image,
    state?.user_session?.user_type,
    state?.user_session?.__t,
    state?.user_session?.user_username,
  ]);

  const refreshMemberships = useCallback(async () => {
    // Legacy member list no longer required; keep empty to avoid redundant fetches
    setMemberChannels([]);
    return true;
  }, []);

  const buildOwnerSummaries = useCallback(async ({ force = false } = {}) => {
    const userId = state?.user_session?._id;
    if (!userId) return [];
    const now = Date.now();
    if (!force) {
      const { timestamp, data } = ownerSummaryCacheRef.current || {};
      if (timestamp && now - timestamp < OWNER_SUMMARY_CACHE_MS) {
        return Array.isArray(data) ? data : [];
      }
      if (ownerFetchPromiseRef.current) {
        try {
          return await ownerFetchPromiseRef.current;
        } catch (err) {
          console.debug('Owner summary fetch previously failed, retrying:', err);
        }
      }
    }
    try {
      const fetchPromise = (async () => {
        const { response, data } = await ChannelsService.getByOwner(userId);
        if (!response?.ok) return [];
        const list = Array.isArray(data?.data) ? data.data : [];
        const handler = stateHandlerRef.current;
        const existingSummaries = handler ? handler.getValue('channelSummaries') || {} : {};

        const enriched = await Promise.all(
          list.map(async (channel) => {
            const resolvedId =
              resolveChannelId(channel?._id)
              || resolveChannelId(channel?.id)
              || resolveChannelId(channel?.channelId)
              || null;
            if (!resolvedId) return null;

            const previousSummary = existingSummaries[resolvedId];
            const channelActivity = getTimeValue(
              channel?.lastActivityAt
              || channel?.lastActivity
              || channel?.updatedAt
              || channel?.updated_at
              || channel?.createdAt
            );
            const previousActivity = getTimeValue(previousSummary?.lastActivity);

            if (
              previousSummary
              && previousSummary.lastMessage
              && channelActivity > 0
              && channelActivity <= previousActivity
            ) {
              return {
                ...previousSummary,
                channelId: resolvedId,
                channel,
                unreadCount: parseUnreadCount(previousSummary.unreadCount),
                previewText:
                  previousSummary.previewText
                  || buildMessagePreviewText(previousSummary.lastMessage),
              };
            }

            try {
              const { response: msgRes, data: msgData } = await ChannelMessagesService.getByChannel(resolvedId, { limit: 1 });
              let lastMessage = null;
              if (msgRes?.ok && Array.isArray(msgData?.data) && msgData.data.length > 0) {
                const rawMessage = msgData.data[0];
                if (rawMessage && typeof rawMessage === 'object') {
                  const createdAt = rawMessage.createdAt || rawMessage.created_at || rawMessage.timestamp || null;
                  lastMessage = createdAt && !rawMessage.createdAt
                    ? { ...rawMessage, createdAt }
                    : rawMessage;
                }
              }

              const meta = msgData?.meta && typeof msgData.meta === 'object' ? msgData.meta : {};
              const unreadCount = parseUnreadCount(
                meta.unreadCount
                ?? meta.unread
                ?? meta.unreadMessages
                ?? msgData?.unreadCount
                ?? channel?.unreadCount
                ?? channel?.unread
                ?? previousSummary?.unreadCount
                ?? 0
              );
              const readState = meta.readState || previousSummary?.readState || null;
              const lastActivity =
                lastMessage?.createdAt
                || lastMessage?.created_at
                || channel?.lastActivityAt
                || channel?.lastActivity
                || channel?.updatedAt
                || channel?.updated_at
                || channel?.createdAt
                || null;
              const previewText = buildMessagePreviewText(lastMessage) || previousSummary?.previewText || '';

              return {
                channelId: resolvedId,
                channel,
                lastMessage,
                lastActivity,
                unreadCount,
                readState,
                previewText,
              };
            } catch (err) {
              console.debug('Failed to fetch last message for owned channel', resolvedId, err);
              const fallbackUnread = parseUnreadCount(
                previousSummary?.unreadCount
                ?? channel?.unreadCount
                ?? channel?.unread
                ?? 0
              );
              return {
                channelId: resolvedId,
                channel,
                lastMessage: previousSummary?.lastMessage || null,
                lastActivity:
                  previousSummary?.lastActivity
                  || channel?.lastActivityAt
                  || channel?.updatedAt
                  || channel?.createdAt
                  || null,
                unreadCount: fallbackUnread,
                readState: previousSummary?.readState || null,
                previewText: previousSummary?.previewText || '',
              };
            }
          })
        );
        const filtered = enriched.filter(Boolean);
        ownerSummaryCacheRef.current = { timestamp: Date.now(), data: filtered };
        return filtered;
      })();

      ownerFetchPromiseRef.current = fetchPromise;
      try {
        return await fetchPromise;
      } finally {
        ownerFetchPromiseRef.current = null;
      }
    } catch (err) {
      console.debug('Failed to fetch owner channels', err);
      return [];
    }
  }, [state?.user_session?._id]);

  const refreshSummary = useCallback(async ({ forceMembership = false, forceOwner = false } = {}) => {
    try {
      const now = Date.now();

      // Prefer unified conversations from backend if available
      const membershipPromise = (async () => {
        // If cache is valid, do not hit network at all
        const cache = membershipSummaryCacheRef.current;
        const cacheValid = !forceMembership
          && cache.timestamp
          && now - cache.timestamp < MEMBERSHIP_SUMMARY_CACHE_MS
          && Array.isArray(cache.data);

        if (cacheValid) {
          return Promise.resolve(cache);
        }

        // If a request is already in-flight, reuse it
        if (!forceMembership && membershipFetchPromiseRef.current) {
          return membershipFetchPromiseRef.current;
        }

        // Try unified conversations endpoint first
        const fetchPromise = (async () => {
          try {
            const unified = await ChannelsService.getConversations();
            if (unified?.response?.ok && Array.isArray(unified?.data?.data)) {
              const payload = {
                timestamp: Date.now(),
                data: unified.data.data,
                totalDocs: typeof unified.data.totalDocs === 'number' ? unified.data.totalDocs : unified.data.data.length,
                source: 'conversations',
              };
              membershipSummaryCacheRef.current = payload;
              return payload;
            }
          } catch (_) {
            // ignore and fallback to legacy
          }

          try {
            const { response, data } = await ChannelsService.getMembershipSummary();
            if (response?.ok && Array.isArray(data?.data)) {
              const payload = {
                timestamp: Date.now(),
                data: data.data,
                totalDocs: typeof data.totalDocs === 'number' ? data.totalDocs : data.data.length,
                source: 'membership',
              };
              membershipSummaryCacheRef.current = payload;
              return payload;
            }
            const fallback = { timestamp: Date.now(), data: [], totalDocs: 0, source: 'membership' };
            membershipSummaryCacheRef.current = fallback;
            return fallback;
          } catch (err) {
            membershipSummaryCacheRef.current = { timestamp: 0, data: [], totalDocs: 0, source: 'membership' };
            throw err;
          }
        })();

        membershipFetchPromiseRef.current = fetchPromise
          .then((payload) => {
            membershipFetchPromiseRef.current = null;
            return payload;
          })
          .catch((err) => {
            membershipFetchPromiseRef.current = null;
            throw err;
          });

        return membershipFetchPromiseRef.current;
      })();

      const membershipSummary = await membershipPromise;
      const shouldFetchOwner = membershipSummary?.source !== 'conversations';
      const ownerSummaries = shouldFetchOwner
        ? await buildOwnerSummaries({ force: forceOwner })
        : [];

      const summaries = [];
      if (membershipSummary && Array.isArray(membershipSummary.data)) {
        summaries.push(...membershipSummary.data);
      }

      if (Array.isArray(ownerSummaries)) {
        const membershipIds = new Set(summaries.map((entry) => String(entry.channelId)));
        ownerSummaries.forEach((entry) => {
          if (!membershipIds.has(entry.channelId)) {
            summaries.push(entry);
          }
        });
      }

      setGlobalSummaries(summaries);
    } catch (err) {
      console.debug('Failed to refresh channel summaries:', err);
    }
  }, [buildOwnerSummaries, setGlobalSummaries]);

  const chatActive = Boolean(user_username || creator_username || ownerUsername);

  useEffect(() => {
    let mounted = true;

    async function bootstrap() {
      try {
        setLoading(true);
        await Promise.all([
          refreshMemberships(),
          refreshSummary({ forceMembership: true, forceOwner: true }),
        ]);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    bootstrap();

    const onFocus = () => {
      refreshMemberships();
      refreshSummary();
    };
    window.addEventListener('focus', onFocus);

    return () => {
      mounted = false;
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshMemberships, refreshSummary]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      refreshSummary();
    }, MEMBERSHIP_SUMMARY_POLL_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [refreshSummary]);

  const channelUnreadUpdated = state?.channelUnreadUpdated;
  const hasOpenChat = Boolean(user_username || creator_username || ownerUsername);
  const showEmptyState = !hasOpenChat && !loading;
  const isChannelsView = messagesType === 'channels';
  const emptyIllustrationSrc = isChannelsView
    ? '/assets/svg/Channel_Illustration.png'
    : '/assets/svg/Chat_Illustration.png';
  const emptyText = isChannelsView
    ? (
      memberChannels.length === 0
        ? t('layout.channels_empty_join')
        : t('layout.channels_empty_select')
    )
    : t('layout.chats_empty_select');

  useEffect(() => {
    if (!channelUnreadUpdated) return;

    ownerSummaryCacheRef.current.timestamp = 0;
    membershipSummaryCacheRef.current.timestamp = 0;
    refreshSummary({ forceMembership: true, forceOwner: true });
    const handler = stateHandlerRef.current;
    if (handler) {
      handler.set('channelUnreadUpdated', false);
    }
  }, [channelUnreadUpdated, refreshSummary]);

  return (
    <div className={`channel-layout__container ${chatActive ? 'chat-active' : ''}`}>
      <aside className="channel-layout__sidebar">
        <ChannelMenu
          messagesType={messagesType}
          setMessagesType={setMessagesType}
          memberChannels={memberChannels}
          memberLoading={loading}
        />
      </aside>
      <main className={`channel-layout__chat ${showEmptyState ? 'channel-layout__chat--empty' : ''}`}>
        {hasOpenChat
          ? <Outlet />
          : (
            loading
              ? null
              : (
                <div className="channel-layout__empty-state">
                  <img
                    className="channel-layout__empty-illustration"
                    src={emptyIllustrationSrc}
                    alt={isChannelsView ? 'Channel illustration' : 'Chat illustration'}
                  />
                  <p className="channel-layout__empty-text">{emptyText}</p>
                </div>
              )
          )}
      </main>
    </div>
  );
}
